// Dashboard Metric Cards Component
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  Globe,
  Clock,
  CheckCircle,
  BarChart3,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

const iconMap = {
  globe: Globe,
  clock: Clock,
  "check-circle": CheckCircle,
  chart: BarChart3,
};

export default function DashboardMetricCards({ summary, isLoading }) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="space-y-3">
                <div className="h-4 w-24 bg-muted rounded"></div>
                <div className="h-8 w-20 bg-muted rounded"></div>
                <div className="h-3 w-32 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!summary) return null;

  const metrics = [
    {
      key: "active_ads",
      data: summary.active_ads,
      gradient: "from-green-500 to-emerald-600",
      bgColor: "bg-green-50 dark:bg-green-950/50",
      iconColor: "text-green-600 dark:text-green-400",
      textColor: "text-green-600 dark:text-green-400",
      ringColor: "ring-green-500/10",
    },
    {
      key: "awaiting_setup",
      data: summary.awaiting_setup,
      gradient: "from-orange-500 to-amber-600",
      bgColor: "bg-orange-50 dark:bg-orange-950/50",
      iconColor: "text-orange-600 dark:text-orange-400",
      textColor: "text-orange-600 dark:text-orange-400",
      ringColor: "ring-orange-500/10",
    },
    {
      key: "budget_utilized",
      data: summary.budget_utilized,
      gradient: "from-blue-500 to-cyan-600",
      bgColor: "bg-blue-50 dark:bg-blue-950/50",
      iconColor: "text-blue-600 dark:text-blue-400",
      textColor: "text-blue-600 dark:text-blue-400",
      ringColor: "ring-blue-500/10",
      showProgress: true,
    },
    {
      key: "total_campaigns",
      data: summary.total_campaigns,
      gradient: "from-purple-500 to-violet-600",
      bgColor: "bg-purple-50 dark:bg-purple-950/50",
      iconColor: "text-purple-600 dark:text-purple-400",
      textColor: "text-purple-600 dark:text-purple-400",
      ringColor: "ring-purple-500/10",
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      {metrics.map((metric) => {
        const { data, gradient, bgColor, iconColor, textColor, ringColor, showProgress } = metric;
        if (!data) return null;

        const IconComponent = iconMap[data.icon] || BarChart3;
        const isPositiveTrend = data.trend === "up";

        return (
          <Card 
            key={metric.key} 
            className={cn(
              "group relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1",
              "border-2 hover:border-primary/20",
              ringColor
            )}
          >
            {/* Background Gradient Effect */}
            <div className={cn(
              "absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-300",
              `bg-gradient-to-br ${gradient}`
            )} />
            
            <CardContent className="p-6 relative">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">
                    {metric.key === "budget_utilized" ? "$ Utilized / Total Budget" : data.label}
                  </p>
                </div>
                <div className={cn(
                  "rounded-xl p-3 ring-4 transition-all duration-300 group-hover:scale-110",
                  bgColor,
                  ringColor
                )}>
                  <IconComponent className={cn("h-5 w-5", iconColor)} />
                </div>
              </div>

              <div className="space-y-3">
                {/* Budget card: Utilized (amber) / Total (green) + % utilized */}
                {metric.key === "budget_utilized" && data.total_budget ? (
                  <>
                    <div className="flex items-baseline flex-wrap gap-x-1.5">
                      <span className="text-2xl sm:text-3xl font-bold text-amber-600 dark:text-amber-400">
                        {data.formatted_value}
                      </span>
                      <span className="text-xl text-muted-foreground font-medium">/</span>
                      <span className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400">
                        {data.total_budget}
                      </span>
                    </div>
                    {data.change != null && (
                      <p className="text-sm text-muted-foreground">
                        {data.change}% utilized
                      </p>
                    )}
                    {showProgress && data.change != null && (
                      <Progress value={Math.min(data.change, 100)} className="h-2" />
                    )}
                  </>
                ) : (
                  <>
                    <h3 className="text-4xl font-bold tracking-tight">
                      {data.formatted_value}
                    </h3>
                    {showProgress && data.change && (
                      <div className="space-y-2">
                        <Progress value={data.change} className="h-2" />
                        <p className="text-xs text-muted-foreground">
                          {data.change}% of total budget utilized
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* {data.change_label && (
                  <div className="flex items-center space-x-2">
                    {isPositiveTrend ? (
                      <div className={cn(
                        "flex items-center space-x-1 rounded-full px-2 py-0.5",
                        "bg-green-100 dark:bg-green-950"
                      )}>
                        <ArrowUpRight className="h-3 w-3 text-green-600 dark:text-green-400" />
                        {data.change && (
                          <span className="text-xs font-semibold text-green-600 dark:text-green-400">
                            +{data.change}
                          </span>
                        )}
                      </div>
                    ) : data.trend === "down" ? (
                      <div className={cn(
                        "flex items-center space-x-1 rounded-full px-2 py-0.5",
                        "bg-red-100 dark:bg-red-950"
                      )}>
                        <ArrowDownRight className="h-3 w-3 text-red-600 dark:text-red-400" />
                        {data.change && (
                          <span className="text-xs font-semibold text-red-600 dark:text-red-400">
                            {data.change}
                          </span>
                        )}
                      </div>
                    ) : null}
                    <span className="text-xs text-muted-foreground font-medium">
                      {data.change_label}
                    </span>
                  </div>
                )} */}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
